import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import * as Popover from '@radix-ui/react-popover';
import { ChevronDown, Settings } from 'lucide-react';
import { useScreenSize } from '@/hooks';
import ManageCoins from './ManageCoins';
import coinsData from '@/utils/coins';
import styles from './Wallet.module.css';
import socket from '@/utils/socketInstance';
import { C, encode, decode, forceSatoshiFormat, storage, Event, __, wait } from '@/utils';

/**
 * Wallet Component
 * Displays coin dropdown and deposit button in header
 * Similar to SkylaCredit from old codebase
 */
const Wallet = () => {
  const navigate = useNavigate();
  const { isMobile } = useScreenSize();
  const [isOpen, setIsOpen] = useState(false);
  const [coin, setCoin] = useState('INR'); // Display coin (default to INR)
  const [selectedCoin, setSelectedCoin] = useState('INR'); // For dropdown selection
  const [credits, setCredits] = useState({});
  const [showManageCoins, setShowManageCoins] = useState(false);
  const [activeCoins, setActiveCoins] = useState(['1', '2', '3', '4']);
  const [credit, setCredit] = useState(false); // Like old code line 522
  
  // Filter coins based on active coins and exclude SC (SiteCoin) and JPC from dropdown
  const availableCoins = coinsData.filter(coin => 
    coin.preffix !== 'SC' && coin.preffix !== 'JPC' && coin.active
  );
  
  const coins = availableCoins.filter(coin => 
    activeCoins.includes(coin.id.toString())
  );

  useEffect(() => {
    // Load active coins and selected coin from storage
    const storedActiveCoins = localStorage.getItem('active_coins');
    if (storedActiveCoins) {
      setActiveCoins(storedActiveCoins.split(',').map(id => id.trim()));
    } else {
      // Set default active coins if none stored
      const defaultCoins = ['1', '2', '3', '4']; // BTC, INR, USDT, ETH
      setActiveCoins(defaultCoins);
      localStorage.setItem('active_coins', defaultCoins.join(','));
    }
    
    const storedCoin = localStorage.getItem('coin') || 'INR';
    setCoin(storedCoin); // Set display coin (like old code)
    setSelectedCoin(storedCoin); // Set selected coin for dropdown
    
    // Load stored credits object from localStorage (persist across refreshes)
    const storedCredits = storage.getKey('credits');
    if (storedCredits && typeof storedCredits === 'object' && !Array.isArray(storedCredits)) {
      console.log('💰 Loading stored credits on mount:', storedCredits);
      setCredits(storedCredits);
      
      // Set current coin's credit from stored credits
      const currentCoinKey = __.lowerCase(storedCoin);
      const currentCreditValue = storedCredits[currentCoinKey];
      if (currentCreditValue !== undefined && currentCreditValue !== null) {
        const creditNum = typeof currentCreditValue === 'number' ? currentCreditValue : (parseFloat(currentCreditValue) || 0);
        const formattedCredit = forceSatoshiFormat(creditNum, storedCoin);
        setCredit(formattedCredit);
        storage.setKey('credit', formattedCredit);
        console.log(`💰 Restored credit for ${storedCoin}: ${formattedCredit} (from stored credits)`);
      } else {
        // If current coin not in stored credits, set to 0
        const formattedCredit = forceSatoshiFormat(0, storedCoin);
        setCredit(formattedCredit);
        console.log(`💰 No stored credit for ${storedCoin}, setting to 0`);
      }
      
      // Ensure coin state is set (important for icon display)
      setCoin(storedCoin);
    } else {
      // Fallback: Load stored credit if available (single value)
      const storedCredit = storage.getKey('credit');
      if (storedCredit && typeof storedCredit !== 'object' && storedCredit !== false) {
        setCredit(storedCredit);
        console.log(`💰 Restored credit from single value: ${storedCredit}`);
      } else {
        // No stored data, set default
        const defaultCredit = forceSatoshiFormat(0, storedCoin);
        setCredit(defaultCredit);
        console.log(`💰 No stored credit, setting default: ${defaultCredit}`);
      }
    }
    
    // Request credit data from socket
    const requestCredit = (coin) => {
      const token = storage.getKey('token');
      if (token && socket) {
        socket.emit(C.CREDIT, encode({ token, coin }));
      }
    };

    // Handle credit data from socket
    const handleCredit = (data) => {
      console.log('💰 Credit data received:', data);
      
      if (!data || !data.credit) {
        console.warn('⚠️ Invalid credit data received:', data);
        return;
      }
      
      const creditObj = data.credit;
      console.log('💰 Credits object:', creditObj);
      
      // Ensure creditObj is actually an object with coin keys, not the whole response
      if (typeof creditObj !== 'object' || Array.isArray(creditObj)) {
        console.warn('⚠️ Credit data is not a valid object:', creditObj);
        return;
      }
      
      setCredits(creditObj);
      
      // Store entire credits object for persistence across refreshes
      storage.setKey('credits', creditObj);
      
      // Update current coin balance using current coin value (like old code line 662)
      // Always read from storage to get latest value (avoid stale closure)
      const currentCoin = storage.getKey('coin') || 'INR';
      const currentCoinKey = __.lowerCase(currentCoin);
      const currentCreditValue = creditObj[currentCoinKey];
      
      // Ensure we have a valid number value
      let creditNum = currentCreditValue;
      if (creditNum === undefined || creditNum === null || typeof creditNum === 'object') {
        creditNum = 0;
      }
      if (typeof creditNum !== 'number') {
        creditNum = parseFloat(creditNum) || 0;
      }
      
      const currentCredit = forceSatoshiFormat(creditNum, currentCoin);
      
      console.log(`💰 Current coin: ${currentCoin}, Credit value: ${creditNum}, Formatted: ${currentCredit}`);
      
      // Update credit state and ensure coin state is in sync
      setCredit(currentCredit);
      storage.setKey('credit', currentCredit);
      setCoin(currentCoin);
      setSelectedCoin(currentCoin);
    };

    // Handle credit update from socket
    const handleUpdateCredit = (data) => {
      console.log('💰 Credit update received:', data);
      
      if (!data) {
        console.warn('⚠️ Invalid credit update data');
        return;
      }
      
      const creditVal = data.value;
      const coinName = data.coin;
      
      console.log(`💰 Update: ${coinName} = ${creditVal}`);
      
      // Update credits object
      setCredits(prev => {
        const updated = {
          ...prev,
          [__.lowerCase(coinName)]: creditVal
        };
        // Store updated credits object for persistence
        storage.setKey('credits', updated);
        return updated;
      });
      
      // If this is the selected coin, update display
      // Always read from storage to get latest value (avoid stale closure)
      const currentCoin = storage.getKey('coin') || 'INR';
      if (__.lowerCase(coinName) === __.lowerCase(currentCoin)) {
        // Ensure creditVal is a number
        let creditNum = creditVal;
        if (typeof creditNum !== 'number') {
          creditNum = parseFloat(creditNum) || 0;
        }
        const formattedCredit = forceSatoshiFormat(creditNum, currentCoin);
        console.log(`💰 Updating display for ${currentCoin}: ${formattedCredit}`);
        setCredit(formattedCredit);
        storage.setKey('credit', formattedCredit);
        // Ensure coin state is in sync
        setCoin(currentCoin);
      }
    };
    
    // Setup socket listeners for credit updates
    const handleCreditEvent = (data) => handleCredit(decode(data));
    const handleUpdateCreditEvent = (data) => handleUpdateCredit(decode(data));
    
    // Listen to coin_changed events (like old code)
    const handleCoinChanged = (newCoin) => {
      console.log('🪙 Coin changed event received:', newCoin);
      const currentStoredCoin = storage.getKey('coin');
      if (currentStoredCoin !== newCoin) {
        storage.setKey('coin', newCoin);
      }
      setCoin(newCoin);
      setSelectedCoin(newCoin);
      
      // Update credit for new coin from stored credits object
      const storedCredits = storage.getKey('credits') || {};
      const coinKey = __.lowerCase(newCoin);
      let creditVal = storedCredits[coinKey];
      
      // Ensure creditVal is a valid number
      if (creditVal === undefined || creditVal === null || typeof creditVal === 'object') {
        creditVal = 0;
      }
      if (typeof creditVal !== 'number') {
        creditVal = parseFloat(creditVal) || 0;
      }
      
      const formattedCredit = forceSatoshiFormat(creditVal, newCoin);
      setCredit(formattedCredit);
      storage.setKey('credit', formattedCredit);
      console.log(`💰 Updated credit for ${newCoin}: ${formattedCredit} (from stored credits)`);
      
      // Request fresh credit from socket
      const token = storage.getKey('token');
      if (token && socket) {
        console.log('🪙 Requesting fresh credit for changed coin:', newCoin);
        socket.emit(C.CREDIT, encode({ token, coin: newCoin }));
      }
    };
    
    // Listen to credit_update window events (like old code line 626-638)
    const handleCreditUpdate = (e) => {
      const updated = e.detail;
      console.log('💰 Credit update event received:', updated);
      setCredit(updated);
      storage.setKey('credit', updated);
    };
    
    if (socket) {
      console.log('🔌 Setting up socket listeners for credit');
      socket.on(C.CREDIT, handleCreditEvent);
      socket.on(C.UPDATE_CREDIT, handleUpdateCreditEvent);
      
      // Request initial credit data
      const token = storage.getKey('token');
      const initialCoin = storage.getKey('coin') || 'INR';
      if (token) {
        console.log('🪙 Requesting initial credit for coin:', initialCoin);
        requestCredit(initialCoin);
      } else {
        console.warn('⚠️ No token found, cannot request credit');
      }
    } else {
      console.warn('⚠️ Socket not available');
    }
    
    // Setup event listeners
    Event.on('coin_changed', handleCoinChanged);
    window.addEventListener('credit_update', handleCreditUpdate);
    
    // Cleanup
    return () => {
      if (socket) {
        socket.off(C.CREDIT, handleCreditEvent);
        socket.off(C.UPDATE_CREDIT, handleUpdateCreditEvent);
      }
      Event.off('coin_changed', handleCoinChanged);
      window.removeEventListener('credit_update', handleCreditUpdate);
    };
  }, []); // Empty deps - handlers read from storage/state directly
  
  // Request credit when coin changes (like old code line 585-589)
  useEffect(() => {
    const token = storage.getKey('token');
    if (token && coin && socket) {
      console.log('🪙 Coin state changed, requesting credit for:', coin);
      socket.emit(C.CREDIT, encode({ token, coin }));
    }
  }, [coin]);
  
  // Sync coin state with storage (in case storage changes externally)
  useEffect(() => {
    const checkStorageCoin = () => {
      const storedCoin = storage.getKey('coin') || 'INR';
      if (storedCoin !== coin) {
        console.log('🪙 Storage coin changed, syncing state:', storedCoin);
        setCoin(storedCoin);
        setSelectedCoin(storedCoin);
        
        // Update credit from stored credits
        const storedCredits = storage.getKey('credits') || {};
        const coinKey = __.lowerCase(storedCoin);
        let creditVal = storedCredits[coinKey];
        
        // Ensure creditVal is a valid number
        if (creditVal === undefined || creditVal === null || typeof creditVal === 'object') {
          creditVal = 0;
        }
        if (typeof creditVal !== 'number') {
          creditVal = parseFloat(creditVal) || 0;
        }
        
        const formattedCredit = forceSatoshiFormat(creditVal, storedCoin);
        setCredit(formattedCredit);
        storage.setKey('credit', formattedCredit);
      }
    };
    
    // Check immediately
    checkStorageCoin();
    
    // Set up interval to check for storage changes (fallback for external changes)
    const interval = setInterval(checkStorageCoin, 1000);
    
    return () => clearInterval(interval);
  }, [coin]);

  const handleCoinSelect = (coinItem) => {
    const newCoin = coinItem.preffix;
    if (storage.getKey('coin') === newCoin) {
      setIsOpen(false);
      return;
    }
    
    // Update coin state FIRST (synchronously)
    storage.setKey('coin', newCoin);
    setCoin(newCoin); // Update display coin immediately
    setSelectedCoin(newCoin); // Update selected coin immediately
    setIsOpen(false);
    
    // Get credit for selected coin from credits object (like old code line 693)
    const coinKey = __.lowerCase(newCoin);
    let creditVal = credits[coinKey];
    
    console.log('🪙 Coin selected:', newCoin, 'Credit value from credits:', creditVal);
    
    // Ensure creditVal is a number/string, not an object
    if (typeof creditVal === 'object' || creditVal === undefined || creditVal === null) {
      creditVal = 0;
    }
    
    // Convert to number if needed
    if (typeof creditVal !== 'number') {
      creditVal = parseFloat(creditVal) || 0;
    }
    
    // Format and update credit immediately (like old code)
    const formattedCredit = forceSatoshiFormat(creditVal, newCoin);
    setCredit(formattedCredit);
    storage.setKey('credit', formattedCredit);
    
    console.log(`💰 Updated credit for ${newCoin}: ${formattedCredit} (from credits object)`);
    
    // Emit coin_changed event (like old code line 700-702)
    wait(200).then(() => {
      Event.emit('coin_changed', newCoin);
    });
    
    // Request updated credit for new coin from socket (for fresh data)
    const token = storage.getKey('token');
    if (token && socket) {
      console.log('🪙 Requesting fresh credit for coin:', newCoin);
      socket.emit(C.CREDIT, encode({ token, coin: newCoin }));
    }
  };

  const handleDepositClick = () => {
    navigate('/wallet?mode=deposit');
  };

  const handleManageCoinsClick = () => {
    setShowManageCoins(true);
    setIsOpen(false);
  };

  const handleCoinsUpdated = (updatedCoins) => {
    setActiveCoins(updatedCoins);
  };

  // Use coin state for display (like old code uses coin, not selectedCoin)
  const selectedCoinData = coins.find(c => c.preffix === coin) || coins[0] || availableCoins[0];
  
  // Get balance for selected coin with proper formatting (like old code line 731-748)
  // Always read from credits object using current coin to avoid stale values
  const getBalance = () => {
    if (!selectedCoinData || !coin) return '0.00';
    
    // Always read from credits object using current coin (most reliable source)
    const coinKey = __.lowerCase(coin);
    let creditValue = credits[coinKey];
    
    // Ensure creditValue is a valid number
    if (creditValue === undefined || creditValue === null || typeof creditValue === 'object') {
      creditValue = 0;
    }
    
    // Convert to number if it's a string
    if (typeof creditValue !== 'number') {
      creditValue = parseFloat(creditValue) || 0;
    }
    
    // Format the credit value
    const formattedCredit = forceSatoshiFormat(creditValue, coin);
    
    // For fiat currencies, show 2 decimal places (like old code line 746-748)
    if (coin === 'USDT' || coin === 'INR' || coin === 'PKR') {
      return Number(formattedCredit).toFixed(2);
    }
    
    return formattedCredit;
  };
  
  // Get image path with fallback
  const getCoinImage = (imageName) => {
    if (!imageName) return '/assets/images/coins/BTC.webp';
    // Handle special cases
    if (imageName === 'SC.png' || imageName === 'SC.webp') {
      return '/assets/images/token/sc.webp';
    }
    return `/assets/images/coins/${imageName}`;
  };

  return (
    <>
      <div className={styles.walletContainer}>
        {/* Coin Dropdown */}
        <Popover.Root open={isOpen} onOpenChange={setIsOpen}>
          <Popover.Trigger asChild>
            <button className={styles.coinSelector}>
              <img 
                src={getCoinImage(selectedCoinData?.image)} 
                alt={selectedCoinData?.name || 'Coin'}
                className={styles.coinIcon}
                onError={(e) => {
                  // Try .png if .webp fails
                  if (e.target.src.includes('.webp')) {
                    const pngPath = e.target.src.replace('.webp', '.png');
                    e.target.src = pngPath;
                  } else {
                    e.target.src = '/assets/images/coins/BTC.webp';
                  }
                }}
              />
              <span className={styles.coinBalance}>
                {getBalance()}
              </span>
              <ChevronDown size={16} className={styles.chevron} />
            </button>
          </Popover.Trigger>

          <Popover.Portal>
            <Popover.Content className={styles.popoverContent} sideOffset={8}>
              <div className={styles.coinList}>
                {coins.map((coinItem) => {
                  const coinKey = __.lowerCase(coinItem.preffix);
                  let balance = credits[coinKey] || 0;
                  
                  // Ensure balance is a number, not an object
                  if (typeof balance === 'object' || balance === null || balance === undefined) {
                    balance = 0;
                  }
                  if (typeof balance !== 'number') {
                    balance = parseFloat(balance) || 0;
                  }
                  
                  const formatted = forceSatoshiFormat(balance, coinItem.preffix);
                  
                  // For fiat currencies, show 2 decimal places
                  const displayBalance = coinItem.preffix === 'USDT' || coinItem.preffix === 'INR' || coinItem.preffix === 'PKR'
                    ? Number(formatted).toFixed(2)
                    : formatted;
                  
                  return (
                    <button
                      key={coinItem.id}
                      className={`${styles.coinItem} ${coin === coinItem.preffix ? styles.coinItemActive : ''}`}
                      onClick={() => handleCoinSelect(coinItem)}
                    >
                      <div className={styles.coinItemLeft}>
                        <img 
                          src={getCoinImage(coinItem.image)} 
                          alt={coinItem.name}
                          className={styles.coinItemIcon}
                          onError={(e) => {
                            // Try .png if .webp fails
                            if (e.target.src.includes('.webp')) {
                              const pngPath = e.target.src.replace('.webp', '.png');
                              e.target.src = pngPath;
                            } else {
                              e.target.src = '/assets/images/coins/BTC.webp';
                            }
                          }}
                        />
                        <span className={styles.coinItemName}>{coinItem.preffix}</span>
                      </div>
                      <span className={styles.coinItemBalance}>
                        {displayBalance}
                      </span>
                    </button>
                  );
                })}
              </div>
              
              {/* Settings Button */}
              <div className={styles.settingsDivider} />
              <button 
                className={styles.settingsButton}
                onClick={handleManageCoinsClick}
              >
                <Settings size={16} />
                <span>Wallet Settings</span>
              </button>
            </Popover.Content>
          </Popover.Portal>
        </Popover.Root>

        {/* Deposit Button */}
        <button 
          className={styles.depositButton}
          onClick={handleDepositClick}
          aria-label="Deposit"
        >
          {isMobile ? (
            <img
              src="/assets/images/wallet.webp"
              alt="Deposit"
              className={styles.walletIcon}
              loading="lazy"
            />
          ) : (
            'Deposit'
          )}
        </button>
      </div>

      {/* Manage Coins Modal */}
      <ManageCoins
        isOpen={showManageCoins}
        onClose={() => setShowManageCoins(false)}
        onCoinsUpdated={handleCoinsUpdated}
      />
    </>
  );
};

export default Wallet;
